import React, { useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { ExperienceFailure } from '@atlassian/jira-issue-navigator-action-conditional-formatting/src/ui/experience/experience-failure.tsx';
import { JqlProvider } from '@atlassian/jira-business-entity-common/src/controllers/jql-context/index.tsx';
import { useConditionalFormattingEntryPoint } from '@atlassian/jira-issue-navigator-conditional-formatting-sidebar/src/controllers/entrypoint-context/ConditionalFormattingEntrypointProvider.tsx';
import { SidebarFallback } from '@atlassian/jira-issue-navigator-conditional-formatting-sidebar/src/ui/format-rules-sidebar/sidebar/fallback/SidebarFallback.tsx';
import { ErrorState } from '../error-state/index.tsx';
import { useResolvedJql } from '../../../../services/active-jql/index.tsx';
import type { ConditionalFormattingSidebarExperience_view$key } from './__generated__/ConditionalFormattingSidebarExperience_view.graphql';
import type { ConditionalFormattingSidebarExperience_project$key } from './__generated__/ConditionalFormattingSidebarExperience_project.graphql';

export type ConditionalFormattingSidebarExperienceProps = {
	onClose: () => void;
	project: ConditionalFormattingSidebarExperience_project$key | null;
	view?: ConditionalFormattingSidebarExperience_view$key | null;
};

export const ConditionalFormattingSidebarExperience = ({
	onClose,
	project,
	view,
}: ConditionalFormattingSidebarExperienceProps) => {
	const resolvedJql = useResolvedJql();
	const { entryPointReferenceSubject } = useConditionalFormattingEntryPoint();

	const projectData = useFragment(
		graphql`
			fragment ConditionalFormattingSidebarExperience_project on JiraProject {
				projectId
				key
			}
		`,
		project,
	);

	const viewData = useFragment(
		graphql`
			fragment ConditionalFormattingSidebarExperience_view on JiraSpreadsheetView {
				# eslint-disable-next-line @atlassian/relay/must-colocate-fragment-spreads
				...RuleList_view @arguments(first: 20)
			}
		`,
		view,
	);

	const errorFallback = useCallback(
		({ error }: { error: Error }) => (
			<>
				<ExperienceFailure error={error} />
				<ErrorState onClose={onClose} />
			</>
		),
		[onClose],
	);

	if (projectData?.key == null) {
		return null;
	}

	return (
		<JqlProvider jqlContext={`project = '${projectData?.key}'`}>
			<JiraEntryPointContainer
				entryPointReferenceSubject={entryPointReferenceSubject}
				id="conditionalFormattingSidebar"
				packageName="jiraIssueNavigatorActionConditionalFormatting"
				errorFallback={errorFallback}
				teamName="Keystone"
				fallback={<SidebarFallback />}
				runtimeProps={{
					projectId: projectData?.projectId ?? undefined,
					onCloseSidebar: onClose,
					viewFragment: viewData ?? null,
					jql: resolvedJql,
				}}
			/>
		</JqlProvider>
	);
};
